import { create } from "zustand";
import type { SupportedLocale } from "@shared/host/host-context";
import {
  detectInitialLocale,
  persistLocaleSelection,
  resolveSupportedLocale,
} from "../lib/locale-preferences";
import i18n, { changeShellLanguage } from "../i18n";
import { getShellRuntime } from "../infrastructure/shell-runtime/runtime";
import { useAuthStore } from "./auth-store";
import { updateMyLocalePreference } from "../services/user-preferences-service";

type LocaleSyncStatus = "idle" | "saving" | "error";

interface ShellLocaleState {
  locale: SupportedLocale;
  syncStatus: LocaleSyncStatus;
  error?: string;
  hydrateFromProfile: (locale?: string | null) => void;
  setLocale: (locale: SupportedLocale) => Promise<void>;
  clearError: () => void;
}

const initialLocale = detectInitialLocale();
let localeMutationId = 0;

const isLatestLocaleMutation = (mutationId: number) =>
  mutationId === localeMutationId;

const notifyLocalePersistenceFailure = (message: string, locale: SupportedLocale) => {
  getShellRuntime().capabilities.ui.toast({
    id: "shell-locale-persistence-error",
    title: i18n.t("layout:topbar.language.errorTitle", { lng: locale }),
    description: message,
    tone: "error",
    durationMs: 8000,
  });
};

void changeShellLanguage(initialLocale).catch((error: unknown) => {
  console.error("[shell-locale-store] Failed to initialize shell locale", error);
});

export const useShellLocaleStore = create<ShellLocaleState>((set, get) => ({
  locale: initialLocale,
  syncStatus: "idle",
  error: undefined,
  hydrateFromProfile: (locale) => {
    const supportedLocale = resolveSupportedLocale(locale);

    if (!supportedLocale || supportedLocale === get().locale) {
      return;
    }

    set({
      locale: supportedLocale,
      syncStatus: "idle",
      error: undefined,
    });
    persistLocaleSelection(supportedLocale);
    void changeShellLanguage(supportedLocale).catch((error: unknown) => {
      console.error("[shell-locale-store] Failed to hydrate locale from profile", error);
    });
  },
  setLocale: async (locale) => {
    const nextLocale = resolveSupportedLocale(locale);
    if (!nextLocale) {
      return;
    }

    const previousLocale = get().locale;
    if (previousLocale === nextLocale && get().syncStatus !== "error") {
      return;
    }

    const mutationId = ++localeMutationId;
    const previousUserLocale = useAuthStore.getState().user?.locale ?? null;

    set({
      locale: nextLocale,
      syncStatus: "saving",
      error: undefined,
    });
    persistLocaleSelection(nextLocale);
    useAuthStore.getState().setUserLocale(nextLocale);
    await changeShellLanguage(nextLocale);

    if (!isLatestLocaleMutation(mutationId)) {
      return;
    }

    const currentAuth = useAuthStore.getState();
    if (!currentAuth.token || !currentAuth.tenant?.slug) {
      if (!isLatestLocaleMutation(mutationId)) {
        return;
      }

      set({
        syncStatus: "idle",
        error: undefined,
      });
      return;
    }

    try {
      const response = await updateMyLocalePreference(nextLocale);

      if (!isLatestLocaleMutation(mutationId)) {
        return;
      }

      set({
        locale: response.locale,
        syncStatus: "idle",
        error: undefined,
      });
      useAuthStore.getState().setUserLocale(response.locale);
      persistLocaleSelection(response.locale);
      await changeShellLanguage(response.locale);
    } catch (error) {
      if (!isLatestLocaleMutation(mutationId)) {
        return;
      }

      console.error("[shell-locale-store] Failed to persist locale", error);
      const message = i18n.t("layout:topbar.language.error", { lng: previousLocale });

      set({
        locale: previousLocale,
        syncStatus: "error",
        error: message,
      });
      useAuthStore.getState().setUserLocale(previousUserLocale);
      persistLocaleSelection(previousLocale);

      try {
        await changeShellLanguage(previousLocale);
      } catch (rollbackError) {
        console.error(
          "[shell-locale-store] Failed to rollback locale after persistence error",
          rollbackError,
        );
      }

      if (!isLatestLocaleMutation(mutationId)) {
        return;
      }

      notifyLocalePersistenceFailure(message, previousLocale);
      throw error;
    }
  },
  clearError: () => {
    if (get().syncStatus !== "error" && !get().error) {
      return;
    }

    set({
      syncStatus: "idle",
      error: undefined,
    });
  },
}));
